import {
  Document as DocumentT,
  type DocumentVersion,
  getStaticCustomTypeOptions,
  invalidateDocumentListData,
  prepareDocument,
} from "@prismicio/editor-fields"
import { useOnChange } from "@prismicio/editor-support/React"
import { ErrorBoundary } from "@prismicio/editor-ui"
import { captureException } from "@sentry/react"
import { usePrefetchQuery, useSuspenseQuery } from "@tanstack/react-query"
import {
  type PropsWithChildren,
  type ReactElement,
  Suspense,
  useEffect,
  useState,
} from "react"
import { useLocation } from "react-router-dom"
import { z } from "zod"

import { queryClient } from "../app/components/providers/QueryProvider"
import { env } from "../app/env"
import { getRepositoryName, router } from "../app/routing"
import type { DocumentVersionStatus } from "../app/services/core"
import {
  usePrefetchStaticCustomType,
  useStaticCustomType,
} from "../app/services/customTypes"
import { getLiveblocksSessionOptions } from "../app/services/liveblocks"
import {
  getReleaseId,
  invalidateReleaseSizeData,
} from "../app/services/release"
import { useTeamSpaceByCustomTypeId } from "../app/services/role"
import { trackDocumentDetailLoaded } from "../app/services/tracking"
import { CollaborationFieldLevelIndicator } from "./collaboration/CollaborationFieldLevelIndicator"
import { CollaborationRoom } from "./collaboration/CollaborationRoom"
import { CollaborationRoomStateProvider } from "./collaboration/CollaborationRoomStateProvider"
import { Document } from "./Document"
import {
  createDocument,
  type ExistingDocumentData,
  preloadDocument,
  useDocument,
  useDocumentMeta,
  useDocumentMetaTitle,
} from "./documentData"
import { DocumentHasLocalEditsProvider } from "./DocumentHasLocalEditsProvider"
import { getSingletonGroupLangID } from "./DocumentHeader/HeaderButtons/CreateButton"
import { DocumentNotFoundErrorBoundary } from "./DocumentNotFoundErrorBoundary"
import { DocumentProvider } from "./DocumentProvider"
import {
  getDocumentTo,
  useExistingDocumentParams,
  useGoToDocument,
  useNewDocumentParams,
} from "./documentRouting"
import { DocumentSkeleton } from "./DocumentSkeleton"
import {
  findBestDocumentVersion,
  findDocumentVersion,
} from "./findDocumentVersion"
import { LabPersonalizationFeedback } from "./LabPersonalizationFeedback"

export function DocumentDetail() {
  return (
    <HealthMonitor>
      <CollaborationRoomStateProvider>
        <DocumentNotFoundErrorBoundary>
          <DocumentDetailSuspense>
            <DocumentDetailContent />
          </DocumentDetailSuspense>
        </DocumentNotFoundErrorBoundary>
      </CollaborationRoomStateProvider>
    </HealthMonitor>
  )
}

function DocumentDetailContent() {
  usePreload()

  const { documentId } = useExistingDocumentParams()

  if (documentId === "create") {
    return <CreateDocument />
  }

  return <ExistingDocument />
}

function CreateDocument() {
  const { releaseId, customTypeId, locale, groupLangId } =
    useNewDocumentParams()

  useSuspenseQuery({
    queryKey: [
      "create-document",
      { releaseId, customTypeId, locale, groupLangId },
    ],
    queryFn: () =>
      createNewDocument({
        customTypeId,
        locale,
        releaseId,
        groupLangId,
      }),
    // We don't care about the entry in the cache.
    // We want to refetch every time this mounts.
    staleTime: 0,
  })

  return null
}

interface CreateNewDocumentArgs {
  customTypeId: string
  locale: string
  releaseId: string | undefined
  groupLangId: string | undefined
}

async function createNewDocument(args: CreateNewDocumentArgs) {
  const { customTypeId, locale, releaseId, groupLangId } = args

  const customType = await queryClient.fetchQuery(
    getStaticCustomTypeOptions({
      id: customTypeId,
      baseUrl: env.PRISMIC_CUSTOM_TYPES_API_BASE_URL,
      authStrategy: env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY,
      repository: getRepositoryName(),
    }),
  )

  const content = prepareDocument({}, customType)
  const singletonGroupLangID = await getSingletonGroupLangID(customType)

  const newDocument = await createDocument({
    customTypeId: customType.id,
    releaseId,
    content,
    title: `Untitled ${new Date().toISOString()}`,
    groupLangId: singletonGroupLangID ?? groupLangId,
    locale,
    tags: [],
  })

  if (releaseId) {
    void invalidateReleaseSizeData(releaseId)
  }

  void invalidateDocumentListData(queryClient)

  const version = findBestDocumentVersion(newDocument)

  const to = getDocumentTo({
    id: newDocument.id,
    status: version.status,
    releaseId,
    archivedVersionId: undefined,
  })

  await router.navigate(to, { replace: true })

  // Cannot return undefined because the query expects a value.
  return null
}

function DocumentDetailSuspense(props: PropsWithChildren) {
  const { children } = props

  const { documentId } = useExistingDocumentParams()
  const [key, setKey] = useState(documentId)

  // We use the documentId as a key for the whole tree.
  // It re-creates the Suspense Boundary only for a big change: When the whole documentId changed (e.g, when going to a different locale).
  // If it's just the versionId that changed (e.g, clicking on the version panel), we have a different, more subtle transition.
  // We have to re-create the Boundary because all react-router changes occur within a startTransition()
  // which doesn't show any fallback if the Suspense boundaries in the new tree are still the same ones, even if we DO have to load a lot of new data.
  useOnChange(documentId, (docId, previousDocId) => {
    // When creating a new doc, never destroy the suspense boundary.
    // This ensures we reuse the same boundary while creating then loading the doc.
    if (previousDocId === "create") return
    setKey(docId)
  })

  return (
    <Suspense fallback={<DocumentSkeleton screen="detail" />} key={key}>
      {children}
    </Suspense>
  )
}

function ExistingDocument() {
  return (
    <DocumentHasLocalEditsProvider>
      <DataLoader>
        {(data) => (
          <CollaborationRoom data={data}>
            <DocumentProvider initData={data}>
              <Document />
              <CollaborationFieldLevelIndicator />
              <LabPersonalizationFeedback />
            </DocumentProvider>
          </CollaborationRoom>
        )}
      </DataLoader>
    </DocumentHasLocalEditsProvider>
  )
}

interface DataLoaderProps {
  children: (data: ExistingDocumentData) => ReactElement
}

function DataLoader(props: DataLoaderProps) {
  const { children } = props

  const { documentId, releaseId, status, archivedVersionId } =
    useExistingDocumentParams()

  // Step 1: Load the document meta; it's always the entry point.
  const meta = useDocumentMeta(documentId)

  // Step 2: Synchronously find our version in the meta.
  const version = findDocumentVersion({
    meta,
    releaseId,
    status,
    archivedVersionId,
  })

  // Step 3: Prefetch document data in parallel.
  usePrefetchStaticCustomType(meta.custom_type_id)
  preloadDocument(version.version_id)

  const initialDraft = useMaybeGetInitialDraft()
  const customType = useStaticCustomType(meta.custom_type_id).data
  const document = useDocument(version.version_id, customType)
  const teamSpace = useTeamSpaceByCustomTypeId(customType.id)

  const documentData: ExistingDocumentData = {
    type: "existing",
    document: initialDraft ?? document,
    isFromInitialDraft: Boolean(initialDraft),
    meta,
    version,
    customType,
    teamSpace,
  }

  useDocumentMetaTitle(documentData)

  const willRedirect = useRedirectToRecentVersion({
    documentId: meta.id,
    recentVersion: version,
    status: version.status,
    releaseId,
    archivedVersionId,
  })

  // No point rendering the wrong existingDocument only to fix that soon after.
  if (willRedirect) return null

  return children(documentData)
}

/*
 * useDocumentVersion tries to return the version data for the versionId we give it.
 * Failing that, it returns its most recent, known version, which happens when
 * using old URLs with a version that is no longer a part of the set of interesting versions.
 * This improves UX as instead of showing 404s all the time, we transparently redirect to that recent version instead.
 */
function useRedirectToRecentVersion(params: {
  documentId: string
  recentVersion: DocumentVersion
  status: DocumentVersionStatus
  releaseId: string | undefined
  archivedVersionId: string | undefined
}) {
  const { documentId, status, recentVersion, releaseId, archivedVersionId } =
    params

  const goToDocument = useGoToDocument()

  const recentVersionReleaseId = getReleaseId(recentVersion)

  const willRedirectArchiveVersion =
    recentVersion.status === "archived" &&
    archivedVersionId !== recentVersion.version_id

  const willRedirect =
    status !== recentVersion.status ||
    releaseId !== recentVersionReleaseId ||
    willRedirectArchiveVersion

  useEffect(() => {
    if (!willRedirect) return

    goToDocument({
      id: documentId,
      status: recentVersion.status,
      releaseId: recentVersionReleaseId,
      archivedVersionId:
        recentVersion.status === "archived"
          ? recentVersion.version_id
          : undefined,
      replace: true,
    })
  }, [
    archivedVersionId,
    documentId,
    goToDocument,
    recentVersion,
    recentVersionReleaseId,
    releaseId,
    willRedirect,
  ])

  return willRedirect
}

function useMaybeGetInitialDraft() {
  const location = useLocation()

  const parsed = locationType.safeParse(location)
  if (!parsed.success) return

  return DocumentT.parse(parsed.data.state.initialDraft)
}

const locationType = z.object({
  state: z.object({
    initialDraft: z.record(z.unknown()),
  }),
})

// It's a big number but some people have very slow connections.
// Reduces the number of false negatives.
const unhealthyLoadingTime = 15_000

function HealthMonitor(props: PropsWithChildren) {
  const { children } = props

  function sendErrorToSentry() {
    captureException(
      new Error(
        `DocumentDetail not loaded after ${unhealthyLoadingTime / 1000}s`,
      ),
    )
  }

  useEffect(() => {
    const timeOnMount = Date.now()

    function onStaticZoneRendered() {
      clearTimeout(unhealthyTimeout)
      removeListener()

      const timeNow = Date.now()
      const timeDiff = (timeNow - timeOnMount) / 1000

      trackDocumentDetailLoaded({ time: timeDiff })
    }

    window.addEventListener(
      "prismic-static-zone-rendered",
      onStaticZoneRendered,
    )

    function removeListener() {
      window.removeEventListener(
        "prismic-static-zone-rendered",
        onStaticZoneRendered,
      )
    }

    const unhealthyTimeout = setTimeout(sendErrorToSentry, unhealthyLoadingTime)

    return () => {
      clearTimeout(unhealthyTimeout)
      removeListener()
    }
  }, [])

  // Catch synchronous errors right before we unmount.
  function renderError(error: unknown): JSX.Element {
    sendErrorToSentry()

    // Delegate back to the App level default boundary.
    throw error
  }

  return <ErrorBoundary renderError={renderError}>{children}</ErrorBoundary>
}

function usePreload() {
  usePrefetchQuery(getLiveblocksSessionOptions())
}
